import React, { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
  fetchDevices,
  forgetDevice,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import { formatClubDateTime } from '../../utils/clubTime';

const APP_NAME = 'Tumbulgum Fishing Club Admin';

type Mode =
  | 'loading'
  | 'choose' // pick authenticator app or email
  | 'totp-setup' // show QR + verify code
  | 'done' // enrolled this session — sign out to finish
  | 'manage'; // already enrolled — devices + add other factor + rotate

type DeviceList = Awaited<ReturnType<typeof fetchDevices>>;

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

const cardClass = 'max-w-md w-full bg-white shadow rounded-lg p-6 sm:p-8 space-y-6';
const primaryBtn =
  'w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-fishing-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50';
const secondaryBtn =
  'w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue disabled:opacity-50';
const inputClass =
  'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900 focus:outline-none focus:ring-fishing-blue focus:border-fishing-blue sm:text-sm';

// Defined at module scope (NOT inside the component) so its identity is stable
// across renders. A component defined inside AdminMFASetupPage would be a new
// type on every keystroke, remounting the subtree and stealing focus from text
// inputs.
const Shell: React.FC<{ title: string; error?: string; children: React.ReactNode }> = ({
  title,
  error,
  children,
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
    <div className={cardClass}>
      <h1 className="text-2xl font-extrabold text-gray-900 text-center">{title}</h1>
      {error && <div className="text-sm text-center text-red-600">{error}</div>}
      {children}
    </div>
  </div>
);

/**
 * Admin MFA setup: enrol an authenticator app (TOTP, recommended) or text
 * message (SMS), and manage trusted devices.
 *
 * Recovery if an admin loses their authenticator is handled out-of-band: an
 * administrator with Cognito access resets the user's MFA in the console and
 * the admin re-enrols on next sign-in. (No self-service backup codes.)
 */
const AdminMFASetupPage: React.FC = () => {
  const { user, isAdminSetupRequired, isAdminsVerified, signOut } = useAuth();

  const [mode, setMode] = useState<Mode>('loading');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // "Continue to admin portal" state — force-refreshes the session so the
  // AdminsVerified group added by the postAuthMfaEnforcer (during this sign-in,
  // after the access token was minted) is reflected without a second login.
  const [continuing, setContinuing] = useState(false);
  const [continueMsg, setContinueMsg] = useState('');

  const [enabledFactors, setEnabledFactors] = useState<string[]>([]);
  const [devices, setDevices] = useState<DeviceList>([]);

  // TOTP
  const [totpUri, setTotpUri] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const refreshState = useCallback(async () => {
    const [{ enabled }, deviceList] = await Promise.all([
      fetchMFAPreference(),
      fetchDevices().catch(() => [] as DeviceList),
    ]);
    setEnabledFactors(enabled ?? []);
    setDevices(deviceList);
    return enabled ?? [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const enabled = await refreshState();
        if (!cancelled) setMode(enabled.length > 0 ? 'manage' : 'choose');
      } catch (err) {
        if (!cancelled) {
          setError(errorMessage(err));
          setMode('choose');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshState]);

  // If the session is already verified (e.g. an admin who navigated here by
  // mistake, or whose group propagated), don't trap them — send them in.
  useEffect(() => {
    if (isAdminsVerified) {
      window.location.href = '/admin';
    }
  }, [isAdminsVerified]);

  // Force a token refresh to pick up the AdminsVerified group set by the
  // PostAuthentication enforcer during this sign-in, then route to the portal.
  // If it's genuinely not active yet (first enrolment needs the factor satisfied
  // at sign-in), guide the user to sign out and back in instead of dead-ending.
  const continueToPortal = async () => {
    setContinuing(true);
    setContinueMsg('');
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const g = (session.tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];
      if (g.includes('AdminsVerified')) {
        window.location.href = '/admin';
        return;
      }
      setContinueMsg(
        'Your second factor isn’t active on this session yet. Please sign out and sign back in to finish.'
      );
    } catch {
      setContinueMsg('Could not refresh your session. Please sign out and sign back in.');
    } finally {
      setContinuing(false);
    }
  };

  // --- TOTP enrolment ---
  const startTotp = async () => {
    setError('');
    setBusy(true);
    try {
      const details = await setUpTOTP();
      const uri = details.getSetupUri(APP_NAME, user?.email || undefined).toString();
      setTotpUri(uri);
      setTotpSecret(details.sharedSecret);
      setMode('totp-setup');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await verifyTOTPSetup({ code: totpCode.trim() });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setTotpCode('');
      setMode('done');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // --- Device management ---
  const handleForgetDevice = async (device: DeviceList[number]) => {
    setError('');
    setBusy(true);
    try {
      await forgetDevice({ device });
      await refreshState();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'loading') {
    return (
      <Shell title="Multi-factor authentication" error={error}>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-fishing-blue" />
        </div>
      </Shell>
    );
  }

  if (mode === 'choose') {
    return (
      <Shell title="Set up multi-factor authentication" error={error}>
        <p className="text-sm text-gray-600 text-center">
          {isAdminSetupRequired
            ? 'Your admin account needs a second factor before you can use the admin portal. Choose a method to get started.'
            : 'Choose a second factor to add to your admin account.'}
        </p>
        <div className="space-y-3">
          <button
            onClick={async () => {
              setError('');
              setBusy(true);
              try {
                await updateMFAPreference({ email: 'PREFERRED' });
                setMode('done');
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            className={primaryBtn}
          >
            Email code (recommended)
          </button>
          <button onClick={startTotp} disabled={busy} className={secondaryBtn}>
            Authenticator app
          </button>
        </div>
        <p className="text-xs text-gray-500 text-center">
          Email code sends a one-time code to your login email — no app install needed. An
          authenticator app (Google Authenticator, Microsoft Authenticator, 1Password) works offline
          and is the most secure option.
        </p>
        {enabledFactors.length > 0 && (
          <button onClick={() => setMode('manage')} className={secondaryBtn}>
            Back to manage
          </button>
        )}
      </Shell>
    );
  }

  if (mode === 'totp-setup') {
    return (
      <Shell title="Scan with your authenticator app" error={error}>
        <div className="flex justify-center">
          {totpUri ? <QRCodeSVG value={totpUri} size={180} level="M" includeMargin /> : null}
        </div>
        <p className="text-xs text-gray-500 text-center break-all">
          Can&apos;t scan? Enter this key manually:
          <br />
          <span className="font-mono text-gray-700">{totpSecret}</span>
        </p>
        <form onSubmit={verifyTotp} className="space-y-4">
          <div>
            <label htmlFor="totp-code" className="block text-sm font-medium text-gray-700">
              Enter the 6-digit code from the app
            </label>
            <input
              id="totp-code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              required
              className={inputClass}
              placeholder="123456"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
            />
          </div>
          <button type="submit" disabled={busy} className={primaryBtn}>
            {busy ? 'Verifying…' : 'Verify and enable'}
          </button>
        </form>
        <button onClick={() => setMode('choose')} disabled={busy} className={secondaryBtn}>
          Back
        </button>
      </Shell>
    );
  }

  if (mode === 'done') {
    return (
      <Shell title="Multi-factor authentication enabled" error={error}>
        <p className="text-sm text-gray-700">
          Your second factor is set up. To finish, sign out and sign back in — your MFA only takes
          effect on your next sign-in.
        </p>
        <button onClick={() => signOut()} className={primaryBtn}>
          Sign out to finish
        </button>
        <button onClick={() => setMode('manage')} className={secondaryBtn}>
          Manage methods and devices
        </button>
      </Shell>
    );
  }

  // mode === 'manage'
  const hasTotp = enabledFactors.includes('TOTP');
  const hasEmail = enabledFactors.includes('EMAIL');

  return (
    <Shell title="Multi-factor authentication" error={error}>
      {isAdminSetupRequired && (
        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
          Sign out and sign back in to finish — your second factor takes effect on your next
          sign-in.
        </div>
      )}

      <div>
        <h2 className="text-sm font-semibold text-gray-900">Your methods</h2>
        <ul className="mt-2 text-sm text-gray-700 space-y-1">
          <li>Email code: {hasEmail ? 'enabled' : 'not set up'}</li>
          <li>Authenticator app: {hasTotp ? 'enabled' : 'not set up'}</li>
        </ul>
      </div>

      {enabledFactors.length > 0 && (
        <div className="space-y-2">
          <button onClick={continueToPortal} disabled={continuing} className={primaryBtn}>
            {continuing ? 'Checking…' : 'Continue to admin portal'}
          </button>
          {continueMsg && (
            <p className="text-sm text-center text-amber-700">{continueMsg}</p>
          )}
        </div>
      )}

      <div className="space-y-2">
        {!hasEmail && (
          <button
            onClick={async () => {
              setError('');
              setBusy(true);
              try {
                await updateMFAPreference({ email: 'PREFERRED' });
                await refreshState();
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            className={primaryBtn}
          >
            Enable email code
          </button>
        )}
        {!hasTotp && (
          <button onClick={startTotp} disabled={busy} className={secondaryBtn}>
            Add authenticator app
          </button>
        )}
        {hasTotp && (
          <button onClick={startTotp} disabled={busy} className={secondaryBtn}>
            Rotate authenticator app (new secret)
          </button>
        )}
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-900">Trusted devices</h2>
        {devices.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">No remembered devices.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {devices.map((device) => (
              <li
                key={device.id}
                className="flex items-center justify-between text-sm border border-gray-200 rounded-md p-2"
              >
                <span className="text-gray-700">
                  {device.name || device.id}
                  {device.lastAuthenticatedDate && (
                    <span className="block text-xs text-gray-400">
                      Last used: {formatClubDateTime(device.lastAuthenticatedDate)}
                    </span>
                  )}
                </span>
                <button
                  onClick={() => handleForgetDevice(device)}
                  disabled={busy}
                  className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                >
                  Forget device
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button onClick={() => signOut()} className={secondaryBtn}>
        Sign Out
      </button>
    </Shell>
  );
};

export default AdminMFASetupPage;
